import type { ModeloDeAnamnese, Pergunta } from "./modelo";

// Questionário padrão (Free). RASCUNHO CLÍNICO: as perguntas precisam da validação de um
// cirurgião-dentista parceiro antes de a anamnese ser ligada em produção. Ids são estáveis — trocar
// o texto de uma pergunta já respondida pede versão nova do modelo.

const simNao = (id: string, texto: string, extra: Partial<Pergunta> = {}): Pergunta => ({ id, texto, tipo: "SIM_NAO", obrigatoria: true, ...extra });
const comDor = { pergunta: "sente_dor", valores: ["SIM"] };

export const ANAMNESE_PADRAO: ModeloDeAnamnese = {
  codigo: "PADRAO",
  versao: 1,
  rascunhoClinico: true,
  secoes: [
    {
      id: "queixa",
      titulo: "Motivo da consulta",
      perguntas: [
        { id: "motivo", texto: "Qual o motivo da sua consulta?", tipo: "TEXTO", obrigatoria: true },
        simNao("sente_dor", "Está sentindo dor?"),
        { id: "dor_local", texto: "Onde dói?", tipo: "TEXTO", mostrarSe: comDor },
        { id: "dor_intensidade", texto: "De 0 a 10, qual a intensidade da dor?", tipo: "ESCALA", mostrarSe: comDor },
        {
          id: "dor_inicio",
          texto: "Desde quando?",
          tipo: "ESCOLHA",
          mostrarSe: comDor,
          opcoes: [
            { valor: "HOJE", rotulo: "Hoje" },
            { valor: "DIAS", rotulo: "Há alguns dias" },
            { valor: "SEMANAS", rotulo: "Há semanas" },
            { valor: "MESES", rotulo: "Há meses" },
          ],
        },
        {
          id: "dor_gatilhos",
          texto: "O que provoca ou piora a dor?",
          tipo: "MULTIPLA",
          mostrarSe: comDor,
          opcoes: [
            { valor: "FRIO", rotulo: "Frio" },
            { valor: "QUENTE", rotulo: "Quente" },
            { valor: "DOCE", rotulo: "Doce" },
            { valor: "MASTIGAR", rotulo: "Mastigar" },
            { valor: "NOITE", rotulo: "Piora à noite" },
            { valor: "ESPONTANEA", rotulo: "Dói sozinha" },
          ],
        },
      ],
    },
    {
      id: "saude",
      titulo: "Saúde geral",
      descricao: "Essas informações ajudam a escolher anestésicos, medicamentos e cuidados com segurança.",
      perguntas: [
        simNao("tratamento_medico", "Está em tratamento médico?", { detalhe: { rotulo: "Qual tratamento?" } }),
        simNao("medicamentos", "Usa algum medicamento?", { detalhe: { rotulo: "Quais? Nome, dose e frequência", obrigatorio: true } }),
        simNao("anticoagulante", "Usa anticoagulante ou AAS (ex.: varfarina, clopidogrel, aspirina)?", {
          detalhe: { rotulo: "Qual?" },
          alerta: { codigo: "ANTICOAGULANTE", quando: ["SIM"] },
        }),
        simNao("bifosfonato", "Usa ou já usou remédio para osteoporose (ex.: alendronato) ou para tratamento de câncer nos ossos?", {
          detalhe: { rotulo: "Qual e por quanto tempo?" },
          alerta: { codigo: "BIFOSFONATO", quando: ["SIM"] },
        }),
        simNao("alergia", "Tem alergia a algum medicamento, anestésico, látex ou alimento?", {
          detalhe: { rotulo: "A quê? Qual foi a reação?", obrigatorio: true },
          alerta: { codigo: "ALERGIA", quando: ["SIM"] },
        }),
        simNao("hipertensao", "Tem pressão alta?", { alerta: { codigo: "HIPERTENSAO", quando: ["SIM"] } }),
        simNao("cardiopatia", "Tem ou já teve problema no coração (infarto, arritmia, sopro, válvula, marca-passo)?", {
          detalhe: { rotulo: "Qual?" },
          alerta: { codigo: "CARDIOPATIA", quando: ["SIM"] },
        }),
        simNao("diabetes", "Tem diabetes?", { detalhe: { rotulo: "Tipo e como está o controle" }, alerta: { codigo: "DIABETES", quando: ["SIM"] } }),
        simNao("sangramento", "Já teve sangramento difícil de parar (depois de extração ou corte) ou tem problema de coagulação?", {
          detalhe: { rotulo: "Conte o que aconteceu" },
          alerta: { codigo: "COAGULACAO", quando: ["SIM"] },
        }),
        simNao("imunidade", "Tem ou trata alguma condição que baixa a imunidade (quimioterapia, radioterapia, transplante, HIV, corticoide contínuo)?", {
          detalhe: { rotulo: "Qual?" },
          alerta: { codigo: "IMUNOSSUPRESSAO", quando: ["SIM"] },
        }),
        {
          id: "outras_condicoes",
          texto: "Tem ou já teve alguma destas condições?",
          tipo: "MULTIPLA",
          opcoes: [
            { valor: "ASMA", rotulo: "Asma ou bronquite" },
            { valor: "CONVULSAO", rotulo: "Epilepsia ou convulsões" },
            { valor: "RINS", rotulo: "Problema nos rins" },
            { valor: "FIGADO", rotulo: "Hepatite ou problema no fígado" },
            { valor: "TIREOIDE", rotulo: "Tireoide" },
            { valor: "AVC", rotulo: "AVC (derrame)" },
            { valor: "NENHUMA", rotulo: "Nenhuma" },
          ],
        },
        { id: "outras_condicoes_detalhe", texto: "Outras condições de saúde ou detalhes", tipo: "TEXTO" },
        simNao("cirurgias", "Já fez cirurgia ou ficou internado(a)?", { detalhe: { rotulo: "Quais e quando?" } }),
        {
          id: "gestacao",
          texto: "Está grávida ou amamentando?",
          tipo: "ESCOLHA",
          obrigatoria: true,
          opcoes: [
            { valor: "GRAVIDA", rotulo: "Estou grávida" },
            { valor: "AMAMENTANDO", rotulo: "Estou amamentando" },
            { valor: "NAO", rotulo: "Não" },
            { valor: "NAO_SE_APLICA", rotulo: "Não se aplica" },
          ],
          alerta: { codigo: "GESTANTE", quando: ["GRAVIDA", "AMAMENTANDO"] },
        },
      ],
    },
    {
      id: "bucal",
      titulo: "Saúde bucal",
      perguntas: [
        {
          id: "ultima_consulta",
          texto: "Quando foi sua última consulta com dentista?",
          tipo: "ESCOLHA",
          opcoes: [
            { valor: "MENOS_6M", rotulo: "Menos de 6 meses" },
            { valor: "6_12M", rotulo: "De 6 meses a 1 ano" },
            { valor: "1_2A", rotulo: "De 1 a 2 anos" },
            { valor: "MAIS_2A", rotulo: "Mais de 2 anos" },
            { valor: "PRIMEIRA", rotulo: "É a primeira vez" },
          ],
        },
        { id: "tratamentos_anteriores", texto: "Quais tratamentos já fez? (canal, aparelho, implante, prótese...)", tipo: "TEXTO" },
        simNao("complicacao", "Já teve complicação em tratamento odontológico ou reação à anestesia?", { detalhe: { rotulo: "O que aconteceu?" } }),
        {
          id: "ansiedade",
          texto: "Sente medo ou ansiedade em tratamento odontológico?",
          tipo: "ESCOLHA",
          opcoes: [
            { valor: "NADA", rotulo: "Não" },
            { valor: "POUCO", rotulo: "Um pouco" },
            { valor: "MUITO", rotulo: "Muito" },
          ],
        },
        { id: "sensibilidade", texto: "Tem sensibilidade nos dentes (frio, quente, doce)?", tipo: "SIM_NAO" },
        { id: "sangramento_gengival", texto: "Sua gengiva sangra ao escovar ou usar fio dental?", tipo: "SIM_NAO" },
        {
          id: "higiene",
          texto: "O que você usa na higiene bucal?",
          tipo: "MULTIPLA",
          opcoes: [
            { valor: "ESCOVA", rotulo: "Escova" },
            { valor: "FIO", rotulo: "Fio dental" },
            { valor: "ENXAGUANTE", rotulo: "Enxaguante" },
            { valor: "INTERDENTAL", rotulo: "Escova interdental" },
          ],
        },
        {
          id: "escovacoes",
          texto: "Quantas vezes escova os dentes por dia?",
          tipo: "ESCOLHA",
          opcoes: [
            { valor: "1", rotulo: "1 vez" },
            { valor: "2", rotulo: "2 vezes" },
            { valor: "3+", rotulo: "3 ou mais" },
          ],
        },
        { id: "bruxismo", texto: "Range ou aperta os dentes (de dia ou dormindo)?", tipo: "SIM_NAO" },
        {
          id: "fumo",
          texto: "Fuma?",
          tipo: "ESCOLHA",
          opcoes: [
            { valor: "NAO", rotulo: "Não" },
            { valor: "SIM", rotulo: "Sim" },
            { valor: "PAROU", rotulo: "Parei" },
          ],
        },
        {
          id: "habitos",
          texto: "Tem algum destes hábitos?",
          tipo: "MULTIPLA",
          opcoes: [
            { valor: "UNHAS", rotulo: "Roer unhas" },
            { valor: "OBJETOS", rotulo: "Morder objetos" },
            { valor: "BOCA_ABERTA", rotulo: "Respirar pela boca" },
            { valor: "ALCOOL", rotulo: "Bebida alcoólica frequente" },
            { valor: "NENHUM", rotulo: "Nenhum" },
          ],
        },
      ],
    },
    {
      id: "observacoes",
      titulo: "Observações",
      perguntas: [{ id: "observacoes", texto: "Algo mais que o dentista deva saber?", tipo: "TEXTO" }],
    },
  ],
};

const MODELOS = [ANAMNESE_PADRAO];

/** O modelo exato que uma anamnese usou (código + versão). */
export function modeloDaAnamnese(codigo: string, versao: number): ModeloDeAnamnese {
  const m = MODELOS.find((x) => x.codigo === codigo && x.versao === versao);
  if (!m) throw new Error(`Modelo de anamnese desconhecido: ${codigo} v${versao}`);
  return m;
}
